import React, { useEffect, useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { renderPDFThumbnail } from "@/lib/fileLoader";

export default function PdfPagePickerDialog({ pdf, fileName, open, onOpenChange, onPick }) {
  const [thumbs, setThumbs] = useState([]);
  const [selected, setSelected] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !pdf) return;
    let cancelled = false;
    setThumbs([]);
    setSelected(1);
    (async () => {
      for (let p = 1; p <= pdf.numPages; p++) {
        const url = await renderPDFThumbnail(pdf, p);
        if (cancelled) return;
        setThumbs((prev) => [...prev, { page: p, url }]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, pdf]);

  const handlePick = async () => {
    setBusy(true);
    try {
      await onPick(selected);
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl" data-testid="pdf-page-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-500" /> Choose a page
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {fileName} has {pdf?.numPages} pages. Pick the floor you want to analyse.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-[55vh] overflow-y-auto p-1" data-testid="pdf-page-grid">
          {Array.from({ length: pdf?.numPages || 0 }, (_, i) => i + 1).map((p) => {
            const t = thumbs.find((x) => x.page === p);
            const isSel = selected === p;
            return (
              <button
                key={p}
                type="button"
                data-testid={`pdf-page-thumb-${p}`}
                onClick={() => setSelected(p)}
                onDoubleClick={() => { setSelected(p); handlePick(); }}
                className={`relative rounded-lg border-2 overflow-hidden bg-white transition-colors aspect-[3/4] flex items-center justify-center ${
                  isSel ? "border-amber-500 ring-2 ring-amber-500/30" : "border-slate-800 hover:border-slate-600"
                }`}
              >
                {t ? (
                  <img src={t.url} alt={`Page ${p}`} className="w-full h-full object-contain" />
                ) : (
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                )}
                <span className={`absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded font-mono text-[10px] font-semibold ${isSel ? "bg-amber-500 text-slate-950" : "bg-slate-950/80 text-slate-200"}`}>
                  {p}
                </span>
              </button>
            );
          })}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-slate-900 border-slate-800 hover:bg-slate-800 text-slate-100">Cancel</Button>
          <Button onClick={handlePick} disabled={busy} data-testid="pdf-page-pick-button" className="bg-amber-500 hover:bg-amber-400 text-slate-950">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Use page ${selected}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
